/**
 * File: apps/qbench-cp/reactapp/src/shared/components/FormV2/RichText.js
 *
 * Purpose: Renders the WYSIWYG rich-text editor used by FormV2 for RICH_TEXT field types.
 *
 * Scope: FormV2 input rendering for rich-text entity fields.
 *
 * Tags: customer-portal
 *
 * Overview: Exports RichText, a memoized component wrapping the TinyMCE React Editor, bound to
 *     react-hook-form via useController. It shows a centered Spinner overlay until the editor
 *     signals it has initialized, and configures TinyMCE's toolbar/plugin set and content
 *     styling once via the shared initSettings object. The TinyMCE API key is read from
 *     base64-obscured constants with a fallback key. The attached `transform` strategy reuses
 *     Input's fromServer/toServer/defaultValue behavior unchanged.
 *
 * Exports: default RichText component (with an attached `transform` static property); named
 *     `height` and `initSettings` constants.
 */

import styled from 'styled-components';
import { Spinner } from 'react-bootstrap';
import React, { useState, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { useController } from 'react-hook-form';
import { safeAtob } from 'shared/utils/misc';
import Input from './Input';
import {
  TINYMCE_API_KEY,
  TINYMCE_API_KEY_FALLBACK,
} from 'shared/constants/misc';

export const height = 400;
const toolbar = [
  'undo redo | styleselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | subscript superscript | link | fullscreen',
];
const plugins = [
  'advlist',
  'autolink',
  'lists',
  'link',
  'charmap',
  'preview',
  'anchor',
  'searchreplace',
  'visualblocks',
  'code',
  'fullscreen',
  'insertdatetime',
  'table',
];
const editorStyle =
  'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif; font-size:14px; line-height: 1.5; }';
const apiKey = safeAtob(TINYMCE_API_KEY) || safeAtob(TINYMCE_API_KEY_FALLBACK);
export const initSettings = {
  height,
  toolbar,
  plugins,
  menubar: true,
  promotion: false,
  entity_encoding: 'named',
  content_style: editorStyle,
};

export const Loading = styled.div`
  min-height: ${(p) => p.height || height}px;
`;

/**
 * @type {import('./types').FieldTransformStrategy}
 */
const transform = {
  fromServer: Input.transform.fromServer,
  toServer: Input.transform.toServer,
  defaultValue: Input.transform.defaultValue,
};

const RichText = React.memo(function RichText({
  name,
  control,
  required,
  readOnly,
}) {
  const [isInit, setInit] = useState(false);
  const editorRef = useRef(null);
  const {
    field: { onChange, value, ref },
  } = useController({
    name,
    control,
    rules: { required },
  });

  function handleInit(event, editor) {
    setInit(true);
    editorRef.current = editor;
  }

  return (
    <div className="mt-1 mb-4">
      {!isInit && (
        <Loading className="d-grid place-items-center w-100">
          <Spinner animation="border" variant="secondary" />
        </Loading>
      )}
      <Editor
        ref={ref}
        value={value}
        apiKey={apiKey}
        onInit={handleInit}
        onEditorChange={onChange}
        disabled={readOnly}
        readonly={readOnly}
        init={initSettings}
        cloudChannel="6"
      />
    </div>
  );
});

RichText.transform = transform;
export default RichText;
